/**
 * `localTimeFor(row)` — contact-local time + business-hours status for a
 * Signals row. Composes `./business-hours.ts` (a byte-for-byte copy of
 * mini-apps/action-view/business-hours.ts — see that file's own citation),
 * re-keyed onto `SignalRow`'s contact/account field names: falls back to
 * `account_billing_country` only when `contact_mailing_country` is null
 * (never when it's merely absent from the row), and returns an explicit
 * `unknown` result — never a thrown error — when location is wholly absent.
 */
import { getBusinessHoursStatus, getLocalHour, getLocalTime } from './business-hours';

export type LocalTimeStatus = 'open' | 'closed' | 'unknown';

export interface LocalTimeResult {
  hour: number | null;
  label: string | null;
  status: LocalTimeStatus;
}

/** The four SignalRow fields `localTimeFor` reads — kept narrow so callers don't have to construct a full SignalRow in tests. */
export interface LocalTimeSourceRow {
  contact_mailing_city: string | null;
  contact_mailing_state: string | null;
  contact_mailing_country: string | null;
  account_billing_country: string | null;
}

/**
 * `now` defaults to the real wall clock; `SignalsFlowTable` passes one derived from
 * `useMinuteToken()` so its memo's dependency on that token is genuine (read here and
 * threaded into the business-hours math) rather than an unread extra dependency.
 */
export function localTimeFor(row: LocalTimeSourceRow, now: Date = new Date()): LocalTimeResult {
  const city = row.contact_mailing_city;
  const state = row.contact_mailing_state;
  const country = row.contact_mailing_country ?? row.account_billing_country;

  const hour = getLocalHour(city, state, country, now);
  if (hour == null) {
    return { hour: null, label: null, status: 'unknown' };
  }

  return {
    hour,
    label: getLocalTime(city, state, country, now),
    status: getBusinessHoursStatus(city, state, country, now),
  };
}
